const mapsApiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY?.trim() || "";

const searchFields = ["id", "displayName", "formattedAddress", "googleMapsURI", "rating", "userRatingCount", "primaryType", "primaryTypeDisplayName", "types"];
const detailFields = [
  "id",
  "displayName",
  "formattedAddress",
  "addressComponents",
  "internationalPhoneNumber",
  "websiteURI",
  "googleMapsURI",
  "rating",
  "userRatingCount",
  "primaryType",
  "primaryTypeDisplayName",
  "types"
];

let placesLibraryPromise;

export const placesApiConfigured = Boolean(mapsApiKey);

function component(components = [], type) {
  return components.find((item) => item.types?.includes(type));
}

export function mapGooglePlace(place = {}) {
  const components = place.addressComponents || [];
  const streetNumber = component(components, "street_number")?.longText || "";
  const route = component(components, "route")?.longText || "";
  const city = component(components, "locality")?.longText
    || component(components, "postal_town")?.longText
    || component(components, "sublocality_level_1")?.longText
    || component(components, "administrative_area_level_2")?.longText
    || "";
  const stateComponent = component(components, "administrative_area_level_1");
  const countryComponent = component(components, "country");
  const line1 = [streetNumber, route].filter(Boolean).join(" ")
    || String(place.formattedAddress || "").split(",")[0]?.trim()
    || "";

  return {
    id: place.id || "",
    displayName: place.displayName || "",
    formattedAddress: place.formattedAddress || "",
    internationalPhoneNumber: place.internationalPhoneNumber || "",
    websiteURI: place.websiteURI || "",
    googleMapsURI: place.googleMapsURI || "",
    rating: Number.isFinite(place.rating) ? place.rating : null,
    userRatingCount: Number.isFinite(place.userRatingCount) ? place.userRatingCount : 0,
    primaryType: place.primaryType || "",
    primaryTypeDisplayName: place.primaryTypeDisplayName || "",
    types: Array.isArray(place.types) ? place.types : [],
    address: {
      line1,
      line2: "",
      city,
      state: stateComponent?.shortText || stateComponent?.longText || "",
      postalCode: component(components, "postal_code")?.longText || "",
      countryCode: countryComponent?.shortText || "US",
      formatted: place.formattedAddress || ""
    }
  };
}

async function loadPlacesLibrary() {
  if (globalThis.google?.maps?.importLibrary) return globalThis.google.maps.importLibrary("places");
  if (!placesApiConfigured) throw new Error("Google Places ist noch nicht konfiguriert. Bitte zuerst den eingeschränkten Maps API Key hinterlegen.");
  if (typeof document === "undefined") throw new Error("Google Places kann nur im Browser geladen werden.");

  if (!placesLibraryPromise) {
    placesLibraryPromise = new Promise((resolve, reject) => {
      const callbackName = `__leadmagnetMapsReady${Date.now()}`;
      const script = document.createElement("script");
      const timeout = window.setTimeout(() => reject(new Error("Google Places hat nicht rechtzeitig geantwortet.")), 12000);

      globalThis[callbackName] = () => {
        window.clearTimeout(timeout);
        delete globalThis[callbackName];
        resolve(globalThis.google.maps.importLibrary("places"));
      };
      script.async = true;
      script.onerror = () => {
        window.clearTimeout(timeout);
        delete globalThis[callbackName];
        reject(new Error("Google Places konnte nicht geladen werden. API-Key und Referrer-Beschränkung prüfen."));
      };
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(mapsApiKey)}&loading=async&libraries=places&v=weekly&callback=${callbackName}`;
      document.head.append(script);
    });
  }
  return placesLibraryPromise;
}

export async function searchGooglePlaces(textQuery) {
  const query = textQuery.trim();
  if (!query) return [];
  const { Place } = await loadPlacesLibrary();
  const { places = [] } = await Place.searchByText({
    textQuery: query,
    fields: searchFields,
    language: "en",
    region: "us"
  });
  return places.slice(0, 5).map(mapGooglePlace);
}

export async function fetchGooglePlace(placeId) {
  if (!placeId) throw new Error("Keine Google Place ID vorhanden.");
  const { Place } = await loadPlacesLibrary();
  const place = new Place({ id: placeId, requestedLanguage: "en", requestedRegion: "us" });
  await place.fetchFields({ fields: detailFields });
  return mapGooglePlace(place);
}
